/**
 * This file controls which trackers will be included in the build.
 * Trackers are provided as an env variables
 * include/exclude trackers based on those env variables.
 */
import { getNinjaConfig } from '@/config/ninja';
import { getRegionConfig } from '@/config/region';
import { BRAZE, FACEBOOK, GA, GTM } from '@/const/env.ts';
import { AVAILABLE_TRACKERS } from '@/generated/types.generated.ts';
import { Laquesis } from '@/laquesis/init';
import { getLogger } from '@/logger';
import { Surveys } from '@/surveys/Surveys';
import { BaseTracker } from './BaseTracker';
import { BrazeTracker } from './BrazeTracker';
import { FacebookTracker } from './FacebookTracker';
import { GaTracker } from './GaTracker';
import { GtmTracker } from './GtmTracker';
import { HydraTracker } from './HydraTracker';

const logger = getLogger('registerTrackers');

// TODO: add tests for the file
export function isLaquesisEnabled() {
  const regionConfig = getRegionConfig();
  const pluginList: string[] = regionConfig?.plugins ?? [];
  const siteConfig = regionConfig?.custom?.[getNinjaConfig().siteUrl];

  if (siteConfig?.plugins) {
    pluginList.push(...siteConfig.plugins);
  }

  return pluginList.includes(AVAILABLE_TRACKERS.laquesis);
}

export function getActiveTrackers() {
  const trackers: BaseTracker[] = [];

  if (!getNinjaConfig().isNative) {
    if (FACEBOOK) {
      trackers.push(new FacebookTracker());
    }

    if (GA) {
      trackers.push(new GaTracker());
    }

    if (GTM) {
      trackers.push(new GtmTracker());
    }

    if (BRAZE) {
      trackers.push(new BrazeTracker());
    }
  }

  return trackers;
}

/**
 * Init trackers and returns a function for all trackers that are delayed (laquesis).
 * The delayed trackers need to be initialized after the dataLayer has been processed.
 */
export async function registerTrackers() {
  const trackers = getActiveTrackers();
  const hydra = new HydraTracker();
  const delayedInit: () => Promise<void> = async () => Promise.resolve();

  await hydra.setupListeners();
  await hydra.init();

  if (isLaquesisEnabled()) {
    const surveys = new Surveys();
    const laquesis = new Laquesis();

    // init is inside
    await surveys.setupListeners();
    await laquesis.setupListeners();
  }

  for (const tracker of trackers) {
    try {
      logger.debug('Processing', tracker.trackerName);
      await tracker.setupListeners();
    } catch {
      // pass
    }
  }

  logger.debug('Trackers initialized');

  return delayedInit;
}
